import { SubmissionError } from 'redux-form';
import { replace } from 'react-router-redux';

import _isNil from 'lodash/fp/isNil';
import _pathOr from 'lodash/fp/pathOr';
import _pick from 'lodash/fp/pick';

import http from '../../../../shared/http';
import endpoints from '../../../../../config/endpoints';
import { actions as parentActions } from '../../../editor';
import {
	createSelectOptionsFromSysEnums,
	createSelectValuesUseIntersection
} from '../../../../shared/form-elements/select';

const defaultInitialState = {
	currentLanguage: 'en',
	fileToUpload: null,
	fields: {
		title: '',
		description: '',
		status: '',
		category: '',
		rank_id: null,
		amount_left: null,
		is_exchange_allowed: false,
		is_prioritized: false,
		is_highlighted: false,
		is_on_wheel: false,
		translations: {
			ru: {
				title: '',
				description: ''
			},
			de: {
				title: '',
				description: ''
			},
			vi: {
				title: '',
				description: ''
			},
			th: {
				title: '',
				description: ''
			}
		}
	}
};

const errorReducerFn = (state, action) => {
	if (action.payload.response.status !== 400) {
		return ({ ...state, error: true, showProgressBar: false });
	}
	return state;
};

const prepareFormErrors = errors => {
	const validationsErrors = {};
	errors.forEach(value => {
		if (value.dataPath.length === 0) {
			const key = value.message.substring(value.message.lastIndexOf(' ') + 1);
			validationsErrors[key] = value.message;
		} else {
			const key = value.dataPath.slice(1);
			validationsErrors[key] = value.message;
		}
	});
	return validationsErrors;
};

const createGeneralForm = ({
	namespace, stateNodeName
}) => {

	const MAIN_FORM_SUBMIT_SUCCESS = `${namespace}/${stateNodeName}-submit-success`;
	const MAIN_FORM_SUBMIT_ERROR = `${namespace}/${stateNodeName}-submit-error`;

	const MAIN_FORM_UPLOAD_SUCCESS = `${namespace}/${stateNodeName}-upload-success`;
	const MAIN_FORM_UPLOAD_ERROR = `${namespace}/${stateNodeName}-upload-error`;
	const MAIN_FORM_PREPARATION_FILE = `${namespace}/${stateNodeName}-preparation-file-success`;
	const MAIN_FORM_CHANGE_LANGUAGE = `${namespace}/${stateNodeName}-change-language`;

	const initialState = {
		...defaultInitialState
	};

	const actions = {
		mainFormSubmit: formData => (dispatch, getState) => {
			const { data } = getState().goodsEditor;
			const url = data.id ? `${endpoints.GOODS.BASE}/${data.id}` : endpoints.GOODS.BASE;

			const submitData = {
				...data,
				prioritized_at: !data.prioritized_at
					? formData.is_prioritized ? new Date() : data.prioritized_at // null
					: formData.is_prioritized ? data.prioritized_at : null,
				category_id: _pathOr('all', ['category', 0, 'value'])(formData),
				rank_id: _pathOr(null, ['rank_id', 0, 'value'])(formData),
				status: _pathOr('hidden', ['status', 0, 'value'])(formData),
				..._pick([
					'title', 'description', 'price', 'translations', 'amount_left',
					'is_exchange_allowed', 'is_highlighted', 'is_on_wheel'
				])(formData)
			};

			const request = http({
				url,
				method: !_isNil(data.id) ? 'put' : 'post',
				actions: [ MAIN_FORM_SUBMIT_SUCCESS, MAIN_FORM_SUBMIT_ERROR],
				data: submitData
			});

			return request(dispatch)
				.then(action => {
					if (action.type === MAIN_FORM_SUBMIT_SUCCESS) {
						const { fileToUpload } = getState().goodsEditor.mainForm;

						setTimeout(() => {
							dispatch(parentActions.setDataToNull());
							dispatch(replace('/goods'));
						}, 300);

						if (fileToUpload) {
							actions.uploadFile(action.payload.data.id, fileToUpload, dispatch);
						} else {
							dispatch(parentActions.toggleProgressBar());
							dispatch(parentActions.fetchData(action.payload.data.id));
						}

					}
					if (action.type === MAIN_FORM_SUBMIT_ERROR && action.payload.response.status === 400) {
						const responseErrors = action.payload.response.data;
						throw new SubmissionError(prepareFormErrors(responseErrors));
					}

				});
		},

		mainFormChangeLanguage: lang => dispatch => {
			return dispatch({ type: MAIN_FORM_CHANGE_LANGUAGE, payload: lang });
		},

		preparationFile: (file, nextAction) => dispatch => {
			const reader = new FileReader();
			const data = new FormData();

			data.append('image', file);

			reader.readAsDataURL(file);
			reader.onloadend = function () {
				nextAction(reader.result);
				dispatch({
					type: MAIN_FORM_PREPARATION_FILE,
					payload: data
				});
			};
		},

		uploadFile: (goodId, fileToUpload, dispatch) => {
			const request = http({
				url: endpoints.GOODS.UPLOAD_GOODS_IMAGE.replace('{id}', goodId),
				method: 'put',
				actions: [MAIN_FORM_UPLOAD_SUCCESS, MAIN_FORM_UPLOAD_ERROR],
				data: fileToUpload
			});

			dispatch(parentActions.toggleProgressBar());

			request(dispatch)
				.then(action => {
					if (action.type === MAIN_FORM_UPLOAD_SUCCESS) {
						dispatch(parentActions.toggleProgressBar());
						dispatch(parentActions.fetchData(action.payload.data.id));
					}
				});
		}

	};

	const reducers = {
		[MAIN_FORM_SUBMIT_ERROR]: errorReducerFn,
		[MAIN_FORM_UPLOAD_ERROR]: errorReducerFn,
		[MAIN_FORM_PREPARATION_FILE]: (state, action) => ({
			...state,
			mainForm: {
				...state.mainForm,
				fileToUpload: action.payload
			}
		}),
		[MAIN_FORM_CHANGE_LANGUAGE]: (state, action) => ({
			...state,
			mainForm: {
				...state.mainForm,
				currentLanguage: action.payload
			}
		})
	};

	const selectors = {
		getCurrentLanguage: _pathOr('en', ['goodsEditor', 'mainForm', 'currentLanguage']),
		getMainFormData: state => {
			const { data: good } = state.goodsEditor;
			const category = _pathOr('', 'category', good);
			const status = _pathOr('', 'status', good);
			const rank_id = _pathOr('', 'rank_id', good);
			const statusOptions = createSelectOptionsFromSysEnums(state.root.lists['good-statuses']);
			const selectedRank = createSelectOptionsFromSysEnums(
				state.root.lists['affiliate-ranks']
			).find(ro => ro.value.id === rank_id);
			return {
				initialValues: {
					...state.goodsEditor.data,
					is_prioritized: !!(good && good.prioritized_at),
					category: [{
						value: category.id,
						label: category.name_en
					}],
					status: createSelectValuesUseIntersection(statusOptions, [status]),
					rank_id: selectedRank && [{
						value: selectedRank.value.id,
						label: selectedRank.label.name
					}]
				}
			};
		}
	};

	return {
		initialState,
		actions,
		reducers,
		selectors
	};
};

export default createGeneralForm;



// WEBPACK FOOTER //
// ./frontend/admin-dashboard/src/modules/goods/components/forms/general/index.js